import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router';
import { Button, CircularProgress, Table, TableContainer } from '@mui/material';
import { darken } from 'polished';
import styled from 'styled-components';

import routes from '@/constants/routes';
import { useResources } from '@/contexts/resource-context';
import { getTenantEntities, TenantEntities } from '@/features/canvas/api/tenant-sso';
import { createGroup, createViewer, editGroup, editViewer } from '@/features/publicLinks/api';
import { Form } from '@/features/ui/components/Form';
import { useDebounceValue } from '@/hooks/useDebounce';
import { useShowToast } from '@/hooks/useShowToast';
import { PageWithSidebar } from '@/layouts/PageWithSidebar';
import { Error } from '@/types/api';

import GroupsTable from './components/GroupsTable/GroupsTable';
import Header from './components/Header/Header';
import ViewerTable from './components/ViewerTable/ViewerTable';

type ICreateGroup = { name: string };
type ICreateViewer = ICreateGroup & { email: string };
type IUpdateGroup = ICreateGroup & { id: number };
type IUpdateViewer = ICreateViewer & { id: number };
type IFormData = ICreateViewer | ICreateGroup | IUpdateGroup | IUpdateViewer;

const Access = () => {
  const { showToast } = useShowToast();
  const { resources } = useResources();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entities, setEntities] = useState<TenantEntities>({
    groups: [],
    viewers: [],
  });
  const [maxViewersCount, setMaxViewersCount] = useState<number>();
  const [searchText, setSearchText] = useState('');
  const { debounceValue, setDebounceValue } = useDebounceValue(searchText);
  const [isEdit, setIsEdit] = useState(false);

  const {
    data: entitiesData = { groups: [], viewers: [], maxUsers: 0 },
    isLoading,
    refetch,
  } = useQuery(getTenantEntities.name, getTenantEntities);

  const [selectedView, setSelectedView] = useState<'viewers' | 'groups'>('viewers');
  const [isAddEntityActive, setIsAddEntityActive] = useState(false);

  const { mutateAsync: createViewerMutation } = useMutation(
    ({ name, email }: ICreateViewer) => createViewer({ name, email }),
    {
      onSuccess: (data: TenantEntities['viewers'][number]) => {
        showToast('success', t('access.viewer_created'));
        setEntities(prev => ({
          ...prev,
          viewers: [...prev.viewers, data].sort((a, b) => {
            if (!a.name) return 1;
            if (!b.name) return -1;
            return a.name.localeCompare(b.name);
          }),
        }));
        setIsAddEntityActive(false);
      },
      onError: (err: Error) => showToast('error', err.response.data.message),
    }
  );

  const { mutateAsync: createGroupMutation } = useMutation(
    ({ name }: ICreateGroup) => createGroup({ name }),
    {
      onSuccess: (data: TenantEntities['groups'][number]) => {
        showToast('success', t('access.group_created'));
        setEntities(prev => ({
          ...prev,
          groups: [...prev.groups, data].sort((a, b) => a.name.localeCompare(b.name)),
        }));
        setIsAddEntityActive(false);
      },
      onError: () => showToast('error', t('access.group_save_error')),
    }
  );

  const { mutateAsync: editGroupMutation } = useMutation(
    ({ input }: { input: IUpdateGroup }) => editGroup(input.id, input),
    {
      onSuccess: (data: TenantEntities['groups'][number]) => {
        setEntities(prev => {
          const groupIndex = prev.groups.findIndex(group => group.id === data.id);

          if (groupIndex !== -1) {
            const updatedGroups = [...prev.groups];
            updatedGroups[groupIndex] = data;

            return { ...prev, groups: updatedGroups };
          }

          return prev;
        });
        showToast('success', t('access.group_saved'));
        setIsEdit(false);
      },
      onError: () => showToast('error', t('access.group_save_error')),
    }
  );

  const { mutateAsync: editViewerMutation } = useMutation(
    ({ id, input }: { id: number; input: { email: string; name: string } }) =>
      editViewer(id, input),
    {
      onSuccess: (data: TenantEntities['viewers'][number]) => {
        setEntities(prev => {
          const viewerIndex = prev.viewers.findIndex(item => item.id === data.id);

          if (viewerIndex !== -1) {
            const updatedViewers = [...prev.viewers];
            updatedViewers[viewerIndex] = data;

            return { ...prev, viewers: updatedViewers };
          }

          return prev;
        });
        showToast('success', t('access.viewer_saved'));
        setIsEdit(false);
      },
      onError: (err: Error) => {
        showToast('error', t('error', { message: err.response.data.message }));
        setIsEdit(false);
      },
    }
  );

  useEffect(() => {
    setEntities(entitiesData);
    setMaxViewersCount(entitiesData.maxUsers);
  }, [entitiesData]);

  if (!resources.is_sso) navigate(routes.roadmaps);

  const onSubmit = (formData: IFormData) => {
    if (isEdit && 'id' in formData) {
      if ('email' in formData) {
        showToast('success', t('access.saving_viewer'));
        void editViewerMutation({
          id: formData.id,
          input: formData,
        });
      } else {
        showToast('success', t('access.saving_group'));
        void editGroupMutation({ input: formData });
      }

      return;
    }

    if ('email' in formData) {
      showToast('success', t('access.saving_viewer'));
      void createViewerMutation(formData);
    } else {
      showToast('success', t('access.saving_group'));
      void createGroupMutation(formData);
    }
  };

  return (
    <PageWithSidebar
      gridColumnStart={1}
      gridColumnEnd={4}
      header={
        <Header
          activeView={selectedView}
          setAddEntity={setIsAddEntityActive}
          setEntities={setEntities}
          searchText={searchText}
          setSearchText={setSearchText}
          viewersCount={entities.viewers.length}
          maxViewersCount={maxViewersCount}
          isLoading={isLoading}
        />
      }
    >
      {isLoading ? (
        <LoaderWrapper>
          <StyledLoader />
        </LoaderWrapper>
      ) : (
        <>
          <ButtonWrappers>
            <StyledButton
              onClick={async () => {
                await refetch();
                setSelectedView('viewers');
                setDebounceValue('');
                setSearchText('');
                setIsAddEntityActive(false);
              }}
              isActive={selectedView === 'viewers'}
            >
              {t('access.viewers')}
            </StyledButton>
            <StyledButton
              onClick={async () => {
                await refetch();
                setSelectedView('groups');
                setDebounceValue('');
                setSearchText('');
                setIsAddEntityActive(false);
              }}
              isActive={selectedView === 'groups'}
            >
              {t('access.groups')}
            </StyledButton>
          </ButtonWrappers>
          <TableContainer>
            <Form onSubmit={formData => onSubmit(formData as unknown as IFormData)}>
              <Table sx={{ minWidth: 500 }}>
                {selectedView === 'viewers' ? (
                  <ViewerTable
                    data={entities}
                    isAddEntityActive={isAddEntityActive}
                    setIsAddEntityActive={setIsAddEntityActive}
                    setEntities={setEntities}
                    searchText={debounceValue}
                    setIsEdit={setIsEdit}
                    isEdit={isEdit}
                  />
                ) : (
                  <GroupsTable
                    data={entities}
                    isAddEntityActive={isAddEntityActive}
                    setIsAddEntityActive={setIsAddEntityActive}
                    setEntities={setEntities}
                    searchText={debounceValue}
                    setIsEdit={setIsEdit}
                    isEdit={isEdit}
                  />
                )}
              </Table>
            </Form>
          </TableContainer>
        </>
      )}
    </PageWithSidebar>
  );
};

const LoaderWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoader = styled(CircularProgress)`
  color: '#6767e0';
`;

const ButtonWrappers = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const StyledButton = styled(Button).withConfig({
  shouldForwardProp: prop => !['isActive'].includes(prop),
})<{ isActive: boolean }>`
  text-transform: none;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.palette.brand.primary : theme.palette.brand.white};
  color: ${({ theme, isActive }) =>
    isActive ? theme.palette.brand.white : theme.palette.brand.secondary};
  border-radius: 20px;
  font-weight: 500;

  :hover {
    background-color: ${({ theme, isActive }) =>
      darken(0.1, isActive ? theme.palette.brand.primary : theme.palette.brand.white)};
  }
`;

export default Access;
